import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Plus,
  Trash2,
  Save,
  AlertTriangle,
  CheckCircle,
  Package,
} from 'lucide-react';
import { format } from 'date-fns';
import { clsx } from 'clsx';

const API_BASE = '/api';

interface LineItem {
  id: string;
  sku: string;
  itemName: string;
  quantity: number;
  unitPrice: number;
  vatCode: string;
}

interface OrderFormData {
  customerCode: string;
  customerName: string;
  poNumber: string;
  deliverTo: string;
  deliveryDate: string;
  orderDate: string;
  currency: string;
  warehouseCode: string;
  notes: string;
  items: LineItem[];
}

const initialFormData: OrderFormData = {
  customerCode: '',
  customerName: '',
  poNumber: '',
  deliverTo: '',
  deliveryDate: '',
  orderDate: format(new Date(), 'yyyy-MM-dd'),
  currency: 'GBP',
  warehouseCode: '',
  notes: '',
  items: [
    { id: '1', sku: '', itemName: '', quantity: 1, unitPrice: 0, vatCode: 'V1' },
  ],
};

export default function ManualOrderPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState<OrderFormData>(initialFormData);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Create order mutation
  const createOrder = useMutation({
    mutationFn: async (data: OrderFormData) => {
      const res = await fetch(`${API_BASE}/orders/manual`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error?.message || 'Failed to create order');
      }
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      navigate(`/orders/${data.data.id}/flow`);
    },
  });

  const handleChange = (field: keyof OrderFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleItemChange = (id: string, field: keyof LineItem, value: string | number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) =>
        item.id === id ? { ...item, [field]: value } : item
      ),
    }));
  };

  const addItem = () => {
    setFormData((prev) => ({
      ...prev,
      items: [
        ...prev.items,
        {
          id: Date.now().toString(),
          sku: '',
          itemName: '',
          quantity: 1,
          unitPrice: 0,
          vatCode: 'V1',
        },
      ],
    }));
  };

  const removeItem = (id: string) => {
    if (formData.items.length <= 1) return;
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== id),
    }));
  };

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.customerCode.trim()) newErrors.customerCode = 'Customer code is required';
    if (!formData.customerName.trim()) newErrors.customerName = 'Customer name is required';
    if (!formData.poNumber.trim()) newErrors.poNumber = 'PO number is required';
    if (!formData.orderDate) newErrors.orderDate = 'Order date is required';

    // Validate items
    const hasInvalidItems = formData.items.some(
      (item) => !item.sku.trim() || !item.itemName.trim() || item.quantity <= 0
    );
    if (hasInvalidItems) {
      newErrors.items = 'All items must have SKU, name, and valid quantity';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validate()) {
      createOrder.mutate(formData);
    }
  };

  const calculateTotal = () => {
    return formData.items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate('/orders')} className="p-2 hover:bg-gray-100 rounded-lg">
            <ArrowLeft className="w-5 h-5 text-gray-600" />
          </button>
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Create Manual Order</h2>
            <p className="text-gray-500 mt-1">Enter order details manually</p>
          </div>
        </div>
      </div>

      {/* Error display */}
      {createOrder.error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <h4 className="font-semibold text-red-800">Error Creating Order</h4>
              <p className="text-red-600 mt-1">{createOrder.error.message}</p>
            </div>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Customer Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Customer Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Code *
              </label>
              <input
                type="text"
                value={formData.customerCode}
                onChange={(e) => handleChange('customerCode', e.target.value)}
                placeholder="e.g., CTSC001"
                className={clsx(
                  'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary',
                  errors.customerCode ? 'border-red-300' : 'border-gray-200'
                )}
              />
              {errors.customerCode && (
                <p className="text-red-500 text-sm mt-1">{errors.customerCode}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Customer Name *
              </label>
              <input
                type="text"
                value={formData.customerName}
                onChange={(e) => handleChange('customerName', e.target.value)}
                placeholder="e.g., Tesco Stores Ltd"
                className={clsx(
                  'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary',
                  errors.customerName ? 'border-red-300' : 'border-gray-200'
                )}
              />
              {errors.customerName && (
                <p className="text-red-500 text-sm mt-1">{errors.customerName}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                PO Number *
              </label>
              <input
                type="text"
                value={formData.poNumber}
                onChange={(e) => handleChange('poNumber', e.target.value)}
                placeholder="e.g., PO12345"
                className={clsx(
                  'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary',
                  errors.poNumber ? 'border-red-300' : 'border-gray-200'
                )}
              />
              {errors.poNumber && (
                <p className="text-red-500 text-sm mt-1">{errors.poNumber}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Warehouse Code
              </label>
              <input
                type="text"
                value={formData.warehouseCode}
                onChange={(e) => handleChange('warehouseCode', e.target.value)}
                placeholder="e.g., WH001"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              />
            </div>
          </div>
        </div>

        {/* Order Details */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Order Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Order Date *
              </label>
              <input
                type="date"
                value={formData.orderDate}
                onChange={(e) => handleChange('orderDate', e.target.value)}
                className={clsx(
                  'w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary',
                  errors.orderDate ? 'border-red-300' : 'border-gray-200'
                )}
              />
              {errors.orderDate && (
                <p className="text-red-500 text-sm mt-1">{errors.orderDate}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Date
              </label>
              <input
                type="date"
                value={formData.deliveryDate}
                onChange={(e) => handleChange('deliveryDate', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Currency
              </label>
              <select
                value={formData.currency}
                onChange={(e) => handleChange('currency', e.target.value)}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              >
                <option value="GBP">GBP</option>
                <option value="EUR">EUR</option>
                <option value="USD">USD</option>
              </select>
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Delivery Address
              </label>
              <input
                type="text"
                value={formData.deliverTo}
                onChange={(e) => handleChange('deliverTo', e.target.value)}
                placeholder="Full delivery address"
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary"
              />
            </div>

            <div className="md:col-span-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Special instructions or notes"
                rows={2}
                className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-perfectted-primary resize-none"
              />
            </div>
          </div>
        </div>

        {/* Line Items */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Line Items</h3>
            <button
              type="button"
              onClick={addItem}
              className="flex items-center gap-2 px-3 py-1.5 text-sm text-perfectted-primary hover:bg-perfectted-light rounded-lg transition-colors"
            >
              <Plus className="w-4 h-4" />
              Add Item
            </button>
          </div>

          {errors.items && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{errors.items}</p>
            </div>
          )}

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">SKU *</th>
                  <th className="text-left py-2 px-2 text-sm font-medium text-gray-600">Item Name *</th>
                  <th className="text-right py-2 px-2 text-sm font-medium text-gray-600 w-24">Qty *</th>
                  <th className="text-right py-2 px-2 text-sm font-medium text-gray-600 w-32">Unit Price</th>
                  <th className="text-center py-2 px-2 text-sm font-medium text-gray-600 w-24">VAT</th>
                  <th className="text-right py-2 px-2 text-sm font-medium text-gray-600 w-32">Line Total</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {formData.items.map((item) => (
                  <tr key={item.id}>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        value={item.sku}
                        onChange={(e) => handleItemChange(item.id, 'sku', e.target.value)}
                        placeholder="SKU"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-perfectted-primary text-sm"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="text"
                        value={item.itemName}
                        onChange={(e) => handleItemChange(item.id, 'itemName', e.target.value)}
                        placeholder="Item name"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-perfectted-primary text-sm"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => handleItemChange(item.id, 'quantity', parseInt(e.target.value) || 0)}
                        min="1"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-perfectted-primary text-sm text-right"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <input
                        type="number"
                        value={item.unitPrice}
                        onChange={(e) => handleItemChange(item.id, 'unitPrice', parseFloat(e.target.value) || 0)}
                        min="0"
                        step="0.01"
                        className="w-full px-3 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-perfectted-primary text-sm text-right"
                      />
                    </td>
                    <td className="py-2 px-2">
                      <select
                        value={item.vatCode}
                        onChange={(e) => handleItemChange(item.id, 'vatCode', e.target.value)}
                        className="w-full px-2 py-1.5 border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-perfectted-primary text-sm"
                      >
                        <option value="V1">V1 (20%)</option>
                        <option value="V0">V0 (0%)</option>
                        <option value="V5">V5 (5%)</option>
                      </select>
                    </td>
                    <td className="py-2 px-2 text-right text-sm font-medium text-gray-900">
                      {(item.quantity * item.unitPrice).toFixed(2)}
                    </td>
                    <td className="py-2 px-2">
                      <button
                        type="button"
                        onClick={() => removeItem(item.id)}
                        disabled={formData.items.length <= 1}
                        className="p-1 text-gray-400 hover:text-red-600 disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200">
                  <td colSpan={5} className="py-3 px-2 text-right font-semibold text-gray-600">
                    Total:
                  </td>
                  <td className="py-3 px-2 text-right font-bold text-gray-900">
                    {formData.currency} {calculateTotal().toFixed(2)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>

        {/* Submit */}
        <div className="flex items-center justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="px-6 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={createOrder.isPending}
            className="flex items-center gap-2 px-6 py-2 text-white bg-perfectted-primary rounded-lg hover:bg-perfectted-primary-hover transition-colors disabled:opacity-50"
          >
            {createOrder.isPending ? (
              <>
                <Package className="w-5 h-5 animate-pulse" />
                Creating...
              </>
            ) : (
              <>
                <Save className="w-5 h-5" />
                Create Order
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
}
